import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import {
  fetchStreams, deleteStream, fetchStreamStats, regenerateStreamKey,
} from '../../../store/actions/StreamActions';
import { selectMaxStreams } from '../../../store/selectors/SubscriptionSelectors';
import { selectIsAdmin } from '../../../store/selectors/AuthSelectors';
import swal from 'sweetalert';
import {
  PageHeader, Card, Button, Badge, Dot, Table, Tr, Td, EmptyState, Input, Select,
} from '../../ui';

const statusBadge = (status, isLive) => {
  if (status === 'failover') return <Badge tone="blue"><Dot tone="blue" pulse /> Failover</Badge>;
  if (isLive) return <Badge tone="live"><Dot tone="live" pulse /> Live</Badge>;
  switch (status) {
    case 'broadcasting': return <Badge tone="live">Broadcasting</Badge>;
    case 'active': return <Badge tone="brand">Active</Badge>;
    case 'disconnected': return <Badge tone="warn">Disconnected</Badge>;
    case 'inactive': return <Badge tone="neutral">Inactive</Badge>;
    default: return <Badge tone="neutral">{status}</Badge>;
  }
};

const StreamList = ({
  streams, loading, error, deleting, regenerating,
  fetchStreams, deleteStream, fetchStreamStats, regenerateStreamKey, maxStreams, isAdmin,
}) => {
  const navigate = useNavigate();
  const [showDelete, setShowDelete] = useState(false);
  const [target, setTarget] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [menuFor, setMenuFor] = useState(null);

  const streamCount = streams.length;
  const isAtLimit = !isAdmin && maxStreams > 0 && streamCount >= maxStreams;
  const usagePercent = maxStreams > 0 ? Math.min(Math.round((streamCount / maxStreams) * 100), 100) : 0;
  const usageTone = usagePercent >= 100 ? 'live' : usagePercent >= 80 ? 'warn' : 'good';

  useEffect(() => {
    fetchStreams();
    fetchStreamStats();
    const id = setInterval(() => { fetchStreams(); fetchStreamStats(); }, 30000);
    return () => clearInterval(id);
  }, [fetchStreams, fetchStreamStats]);

  const copy = (text, label) =>
    navigator.clipboard.writeText(text).then(() =>
      swal('Copied!', `${label} copied to clipboard.`, 'success', { timer: 1400, buttons: false }));

  const confirmDelete = () => {
    if (!target) return;
    deleteStream(target.id, (err) => {
      swal(err ? 'Error!' : 'Deleted!', err ? 'Failed to delete stream.' : 'Stream has been deleted.', err ? 'error' : 'success');
      setShowDelete(false);
      setTarget(null);
    });
  };

  const regenKey = (id) => {
    swal({
      title: 'Regenerate Stream Key?',
      text: 'The old key will stop working immediately.',
      icon: 'warning', buttons: ['Cancel', 'Regenerate'], dangerMode: true,
    }).then((ok) => ok && regenerateStreamKey(id, (err) => {
      swal(err ? 'Error!' : 'Success!', err ? 'Failed to regenerate key.' : 'Stream key regenerated.', err ? 'error' : 'success');
      if (!err) fetchStreams();
    }));
  };

  // A team member's stream is owned by the member, but billing/support cares
  // about the paying parent account — show that as the primary identity when
  // one exists (parent_* is only ever set for a team member's stream).
  const hasParent = (s) => s.parent_name || s.parent_company || s.parent_username || s.parent_email;
  const ownerLabel = (s) => (hasParent(s)
    ? (s.parent_name || s.parent_company || s.parent_username || s.parent_email)
    : (s.owner_name || s.owner_company || s.owner_username || s.owner_email || null));
  const ownerEmailFor = (s) => (hasParent(s) ? s.parent_email : s.owner_email);
  const memberLabel = (s) => (hasParent(s) ? (s.owner_name || s.owner_username || s.owner_email) : null);

  const filtered = streams.filter((s) => {
    const q = search.toLowerCase();
    const haystack = [
      s.name, s.stream_key, s.owner_email, s.owner_username, s.owner_name, s.owner_company,
      s.parent_email, s.parent_username, s.parent_name, s.parent_company,
    ].filter(Boolean).join(' ').toLowerCase();
    const matchesSearch = haystack.includes(q);
    const matchesStatus = statusFilter === 'all' || s.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <>
      <PageHeader
        title="Live Streams"
        subtitle="Manage your RTMP ingest points and HLS / DASH outputs"
        actions={
          <>
            {!isAdmin && maxStreams > 0 && (
              <div className="tw-min-w-[160px]">
                <div className="tw-mb-1 tw-flex tw-justify-between tw-text-xs tw-font-semibold">
                  <span className="tw-text-content-muted">Streams used</span>
                  <span className={`tw-text-${usageTone === 'good' ? 'good' : usageTone === 'warn' ? 'warn' : 'live'}`}>
                    {streamCount} / {maxStreams}
                  </span>
                </div>
                <div className="tw-h-1.5 tw-overflow-hidden tw-rounded-full tw-bg-ink-750">
                  <div
                    className={`tw-h-full tw-rounded-full ${usageTone === 'good' ? 'tw-bg-good' : usageTone === 'warn' ? 'tw-bg-warn' : 'tw-bg-live'}`}
                    style={{ width: `${usagePercent}%` }}
                  />
                </div>
              </div>
            )}
            <Button variant="secondary" icon="fa-rotate" onClick={() => { fetchStreams(); fetchStreamStats(); }} disabled={loading}>
              Refresh
            </Button>
            <Button
              variant={isAtLimit ? 'outline' : 'primary'}
              icon={isAtLimit ? 'fa-arrow-up' : 'fa-plus'}
              onClick={() => navigate(isAtLimit ? '/pricing' : '/create-stream')}
            >
              {isAtLimit ? 'Upgrade Plan' : 'Create Stream'}
            </Button>
          </>
        }
      />

      {isAtLimit && (
        <div className="tw-mb-4 tw-rounded-xl tw-border tw-border-warn/30 tw-bg-warn/10 tw-px-4 tw-py-3 tw-text-sm tw-text-warn">
          <strong>Stream limit reached.</strong> Your plan allows {maxStreams} stream{maxStreams !== 1 ? 's' : ''}.
          Delete one or <button className="tw-font-bold tw-underline" onClick={() => navigate('/pricing')}>upgrade your plan</button>.
        </div>
      )}
      {error && (
        <div className="tw-mb-4 tw-rounded-xl tw-border tw-border-live/30 tw-bg-live/10 tw-px-4 tw-py-3 tw-text-sm tw-text-live">{error}</div>
      )}

      <div className="tw-mb-4 tw-flex tw-flex-wrap tw-gap-3">
        <Input
          className="tw-max-w-sm"
          placeholder="Search by name or key…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select className="tw-max-w-[180px]" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">All status</option>
          <option value="broadcasting">Broadcasting</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="disconnected">Disconnected</option>
        </Select>
      </div>

      <Card noPad>
        {filtered.length === 0 ? (
          <EmptyState
            icon="fa-video"
            title={search || statusFilter !== 'all' ? 'No streams match your filters' : 'No streams yet'}
            hint={search || statusFilter !== 'all' ? 'Try clearing the search or status filter.' : 'Create your first stream to get an RTMP URL and key.'}
            action={!search && statusFilter === 'all' && <Button icon="fa-plus" onClick={() => navigate('/create-stream')}>Create Stream</Button>}
          />
        ) : (
          <Table head={[
            'Stream',
            ...(isAdmin ? ['Owner'] : []),
            'Status', 'Stream Key', 'Viewers', 'Created', '',
          ]}>
            {filtered.map((stream) => (
              <Tr key={stream.id}>
                <Td>
                  <div className="tw-flex tw-items-center tw-gap-3">
                    <span className="tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-lg tw-bg-brand-soft tw-text-brand">
                      <i className="fa-solid fa-video" />
                    </span>
                    <div className="tw-min-w-0">
                      <div className="tw-truncate tw-font-semibold tw-text-heading">{stream.name}</div>
                      <div className="tw-truncate tw-text-xs tw-text-content-faint">{stream.description || 'No description'}</div>
                    </div>
                  </div>
                </Td>
                {isAdmin && (
                  <Td>
                    {ownerLabel(stream) ? (
                      <div className="tw-min-w-0">
                        <div className="tw-truncate tw-font-medium tw-text-content">{ownerLabel(stream)}</div>
                        {ownerEmailFor(stream) && ownerLabel(stream) !== ownerEmailFor(stream) && (
                          <div className="tw-truncate tw-text-xs tw-text-content-faint">{ownerEmailFor(stream)}</div>
                        )}
                        {memberLabel(stream) && (
                          <div className="tw-truncate tw-text-xs tw-text-brand">via {memberLabel(stream)} (team)</div>
                        )}
                      </div>
                    ) : (
                      <Badge tone="warn">Unassigned</Badge>
                    )}
                  </Td>
                )}
                <Td>{statusBadge(stream.status, stream.is_live)}</Td>
                <Td>
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <code className="tw-max-w-[160px] tw-truncate tw-rounded tw-bg-ink-800 tw-px-2 tw-py-1 tw-text-xs tw-text-content-muted">
                      {stream.stream_key}
                    </code>
                    <button className="tw-text-content-faint hover:tw-text-heading" title="Copy key" onClick={() => copy(stream.stream_key, 'Stream key')}>
                      <i className="fa-solid fa-copy" />
                    </button>
                    <button className="tw-text-content-faint hover:tw-text-warn" title="Regenerate key" disabled={regenerating} onClick={() => regenKey(stream.id)}>
                      <i className={`fa-solid fa-rotate ${regenerating ? 'fa-spin' : ''}`} />
                    </button>
                  </div>
                </Td>
                <Td>
                  <span className={stream.is_live ? 'tw-font-bold tw-text-good' : 'tw-text-content-muted'}>
                    {stream.viewer_count || 0}
                  </span>
                </Td>
                <Td className="tw-text-content-muted">{new Date(stream.created_at).toLocaleDateString()}</Td>
                <Td className="tw-text-right">
                  <div className="tw-relative tw-inline-block">
                    <Button variant="secondary" size="sm" icon="fa-ellipsis" onClick={() => setMenuFor(menuFor === stream.id ? null : stream.id)} />
                    {menuFor === stream.id && (
                      <>
                        <div className="tw-fixed tw-inset-0 tw-z-10" onClick={() => setMenuFor(null)} />
                        <div className="tw-absolute tw-right-0 tw-z-20 tw-mt-1 tw-w-48 tw-overflow-hidden tw-rounded-xl tw-border tw-border-line tw-bg-ink-850 tw-py-1 tw-text-left tw-shadow-pop">
                          {[
                            ['fa-eye', 'View details', () => navigate(`/streams/${stream.id}`)],
                            ['fa-copy', 'Copy RTMP URL', () => copy(stream.rtmp_url, 'RTMP URL')],
                            ['fa-copy', 'Copy HLS URL', () => copy(stream.hls_url, 'HLS URL')],
                          ].map(([ic, label, fn]) => (
                            <button key={label} className="tw-flex tw-w-full tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-text-sm tw-text-content hover:tw-bg-ink-800" onClick={() => { fn(); setMenuFor(null); }}>
                              <i className={`fa-solid ${ic} tw-w-4 tw-text-content-faint`} />{label}
                            </button>
                          ))}
                          <div className="tw-my-1 tw-border-t tw-border-line" />
                          <button className="tw-flex tw-w-full tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-text-sm tw-text-live hover:tw-bg-ink-800" onClick={() => { setTarget(stream); setShowDelete(true); setMenuFor(null); }}>
                            <i className="fa-solid fa-trash tw-w-4" />Delete
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </Td>
              </Tr>
            ))}
          </Table>
        )}
      </Card>

      <Modal show={showDelete} onHide={() => setShowDelete(false)} centered>
        <Modal.Header closeButton><Modal.Title>Delete stream</Modal.Title></Modal.Header>
        <Modal.Body>
          Delete <strong>{target?.name}</strong>? This cannot be undone and the stream key is permanently removed.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDelete(false)}>Cancel</Button>
          <Button variant="danger" icon="fa-trash" onClick={confirmDelete} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete Stream'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

const mapStateToProps = (state) => ({
  streams: state.streams.streams,
  loading: state.streams.loading,
  error: state.streams.error,
  deleting: state.streams.deleting,
  regenerating: state.streams.regenerating,
  maxStreams: selectMaxStreams(state),
  isAdmin: selectIsAdmin(state),
});

export default connect(mapStateToProps, {
  fetchStreams, deleteStream, fetchStreamStats, regenerateStreamKey,
})(StreamList);
